import { format, parseISO, isValid } from 'date-fns';
import {
  Sprint,
  Task,
  TaskStatus,
  TASK_STATUS_META,
  DailyGoal,
  CallTopic,
  Refinement,
  ScopeItem,
} from '../types/models';

export interface ReportContext {
  sprint: Sprint | null;
  tasks: Task[];
  dailyGoals?: DailyGoal[];
  callTopics?: CallTopic[];
  refinements?: Refinement[];
  scopeItems?: ScopeItem[];
}

const formatRange = (start?: string, end?: string) => {
  const fmt = (s?: string) => {
    if (!s) return '';
    const d = parseISO(s);
    return isValid(d) ? format(d, 'd MMM') : s;
  };
  return `${fmt(start)} - ${fmt(end)}`;
};

const STATUS_ORDER: TaskStatus[] = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'DISCUSSION', 'BLOCKED'];

const groupByAssignee = (tasks: Task[]) => {
  const map = new Map<string, Task[]>();
  for (const t of tasks) {
    const key = t.assignee?.trim() || 'Unassigned';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(t);
  }
  return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
};

// =================================================================
// Plain text
// =================================================================
export const generateTextReport = (ctx: ReportContext): string => {
  const { sprint, tasks } = ctx;
  const out: string[] = [];

  out.push('------------------------------');
  if (sprint) {
    out.push(`${sprint.sprintName} (${formatRange(sprint.startDate, sprint.endDate)})`);
  }
  out.push('------------------------------');
  out.push('');

  if (sprint?.sprintGoal) out.push(sprint.sprintGoal);
  if (sprint?.goalDescription) out.push(sprint.goalDescription);
  if (sprint?.sprintGoal || sprint?.goalDescription) out.push('');

  if (ctx.scopeItems && ctx.scopeItems.length > 0) {
    out.push('------------------------------');
    out.push('Sprint Scope');
    out.push('------------------------------');
    for (const s of ctx.scopeItems) {
      out.push(s.content);
      out.push('');
    }
  }

  out.push('------------------------------');
  out.push('Sprint Tasks');
  out.push('------------------------------');
  out.push('');

  for (const [assignee, list] of groupByAssignee(tasks)) {
    out.push(`${assignee} :`);
    const sorted = [...list].sort(
      (a, b) =>
        STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
        a.ticketNo.localeCompare(b.ticketNo),
    );
    for (const t of sorted) {
      const meta = TASK_STATUS_META[t.status];
      out.push(`    ${meta.icon} ${t.ticketNo} ${t.title}`);
    }
    out.push('');
  }

  if (ctx.dailyGoals && ctx.dailyGoals.length > 0) {
    out.push('------------------------------');
    out.push('Daily Goals');
    out.push('------------------------------');
    const byDate = new Map<string, DailyGoal[]>();
    for (const g of ctx.dailyGoals) {
      if (!byDate.has(g.date)) byDate.set(g.date, []);
      byDate.get(g.date)!.push(g);
    }
    [...byDate.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .forEach(([date, goals]) => {
        out.push('');
        out.push(`[${date}]`);
        for (const g of goals) {
          out.push(`    ${g.assignee} : ${g.goal}`);
        }
      });
    out.push('');
  }

  if (ctx.callTopics && ctx.callTopics.length > 0) {
    out.push('------------------------------');
    out.push('Call Topics');
    out.push('------------------------------');
    for (const ct of ['4PM', '8PM'] as const) {
      const list = ctx.callTopics.filter((t) => t.callType === ct);
      if (list.length === 0) continue;
      out.push('');
      out.push(`${ct} Call:`);
      for (const t of list) {
        out.push(`    - ${t.topic}  (${t.date})`);
      }
    }
    out.push('');
  }

  if (ctx.refinements && ctx.refinements.length > 0) {
    out.push('------------------------------');
    out.push('Refinements');
    out.push('------------------------------');
    for (const r of ctx.refinements) {
      out.push(`    - ${r.topic}${r.notes ? ` — ${r.notes}` : ''}`);
    }
    out.push('');
  }

  return out.join('\n');
};

// =================================================================
// Markdown
// =================================================================
export const generateMarkdownReport = (ctx: ReportContext): string => {
  const { sprint, tasks } = ctx;
  const out: string[] = [];

  if (sprint) {
    out.push(`# ${sprint.sprintName}`);
    out.push(`_${formatRange(sprint.startDate, sprint.endDate)}_`);
    out.push('');
    if (sprint.sprintGoal) {
      out.push(`> **Goal:** ${sprint.sprintGoal}`);
    }
    if (sprint.goalDescription) {
      out.push(`> ${sprint.goalDescription}`);
    }
    out.push('');
  }

  if (ctx.scopeItems && ctx.scopeItems.length > 0) {
    out.push('## Sprint Scope');
    for (const s of ctx.scopeItems) {
      out.push(s.content);
      out.push('');
    }
  }

  out.push('## Sprint Tasks');
  out.push('');

  for (const [assignee, list] of groupByAssignee(tasks)) {
    out.push(`### ${assignee}`);
    const sorted = [...list].sort(
      (a, b) =>
        STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
        a.ticketNo.localeCompare(b.ticketNo),
    );
    for (const t of sorted) {
      const meta = TASK_STATUS_META[t.status];
      out.push(`- ${meta.icon} **${t.ticketNo}** ${t.title}`);
    }
    out.push('');
  }

  if (ctx.dailyGoals && ctx.dailyGoals.length > 0) {
    out.push('## Daily Goals');
    const byDate = new Map<string, DailyGoal[]>();
    for (const g of ctx.dailyGoals) {
      if (!byDate.has(g.date)) byDate.set(g.date, []);
      byDate.get(g.date)!.push(g);
    }
    [...byDate.entries()]
      .sort(([a], [b]) => b.localeCompare(a))
      .forEach(([date, goals]) => {
        out.push(`### ${date}`);
        for (const g of goals) {
          out.push(`- **${g.assignee}:** ${g.goal}`);
        }
        out.push('');
      });
  }

  if (ctx.callTopics && ctx.callTopics.length > 0) {
    out.push('## Call Topics');
    for (const ct of ['4PM', '8PM'] as const) {
      const list = ctx.callTopics.filter((t) => t.callType === ct);
      if (list.length === 0) continue;
      out.push(`### ${ct} Call`);
      for (const t of list) {
        out.push(`- ${t.topic} _(${t.date})_`);
      }
      out.push('');
    }
  }

  if (ctx.refinements && ctx.refinements.length > 0) {
    out.push('## Refinements');
    for (const r of ctx.refinements) {
      out.push(`- ${r.topic}${r.notes ? ` — ${r.notes}` : ''}`);
    }
    out.push('');
  }

  return out.join('\n');
};
